import React, { useContext } from 'react';

import { theme } from 'config/theme';
import { CanvasUnitContext, fitLabel, uprightLabelBox } from '../canvasScale';
import { TableShape } from '../context';
import { tableChairs } from '../tableChairs';

interface TableFigureProps {
    width: number;
    height: number;
    seats: number;
    shape: TableShape;
    color?: string;
    label?: string;
    isActive?: boolean;
    rotation?: number;
}

const DEFAULT_COLOR = theme.palette.primary.main;
const INACTIVE_COLOR = theme.palette.grey['500'];
const LABEL_MAX_FONT = 11;
const OUTLINE_WIDTH = 1.5;

/**
 * Pure SVG table figure, drawn to scale with one chair per seat around the body. Centered at
 * (0,0); the caller translates it into place.
 */
const TableFigure = React.memo(function TableFigure({
    width,
    height,
    seats,
    shape,
    color,
    label,
    isActive = true,
    rotation = 0,
}: TableFigureProps) {
    const unit = useContext(CanvasUnitContext);
    const base = Math.min(width, height);
    // Capped by the body so a table a few px wide is not all outline.
    const strokeWidth = Math.min(OUTLINE_WIDTH * unit, base / 8);
    const effectiveColor = color ?? (isActive ? DEFAULT_COLOR : INACTIVE_COLOR);
    const dash = isActive ? undefined : `${4 * unit} ${2 * unit}`;
    // The label stays upright while the body turns under it.
    const labelBox =
        shape === 'round'
            ? { width: base, height: base }
            : uprightLabelBox(width, height, rotation);
    const fitted = label ? fitLabel(label, labelBox, LABEL_MAX_FONT, unit) : null;

    return (
        <g opacity={isActive ? 1 : 0.5}>
            {label && <title>{label}</title>}
            {/* Chairs and body rotate about the center; the label stays upright. */}
            <g transform={rotation ? `rotate(${rotation})` : undefined}>
                {tableChairs(width, height, seats, shape).map((chair, i) => (
                    <rect
                        key={i}
                        x={chair.x - chair.half}
                        y={chair.y - chair.half}
                        width={chair.half * 2}
                        height={chair.half * 2}
                        rx={chair.half / 3}
                        fill={effectiveColor}
                        stroke="none"
                        opacity={0.85}
                    />
                ))}
                {shape === 'round' ? (
                    <circle
                        r={base / 2}
                        fill="white"
                        stroke={effectiveColor}
                        strokeWidth={strokeWidth}
                        strokeDasharray={dash}
                    />
                ) : (
                    <rect
                        x={-width / 2}
                        y={-height / 2}
                        width={width}
                        height={height}
                        rx={Math.min(2 * unit, base / 4)}
                        fill="white"
                        stroke={effectiveColor}
                        strokeWidth={strokeWidth}
                        strokeDasharray={dash}
                    />
                )}
            </g>
            {fitted && (
                <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={fitted.fontSize}
                    fontWeight={600}
                    fill={effectiveColor}
                    fontFamily="-apple-system, BlinkMacSystemFont, Roboto, Segoe UI, sans-serif"
                    style={{ userSelect: 'none', pointerEvents: 'none' }}
                >
                    {fitted.text}
                </text>
            )}
        </g>
    );
});

export default TableFigure;
